import { useState, useEffect } from 'react';
import { reportApi } from '../../api/report.api';
import { BASE_MEDIA_URL } from '../../api/client';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Skeleton, EmptyState, Badge } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { ClipboardText, DownloadSimple } from '@phosphor-icons/react';

const pad2 = (n) => String(n).padStart(2, '0');
const dateParts = (isoDate) => {
  const [y, m, d] = isoDate.split('-');
  return { year: y, month: pad2(m), day: pad2(d) };
};

export default function PresenceReportPage() {
  const { addToast } = useUI();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [type, setType] = useState('employee');

  const buildParams = (output) => {
    const { year, month, day } = dateParts(date);
    return {
      type,
      output,
      'start_date[year]': year, 'start_date[month]': month, 'start_date[day]': day,
      'end_date[year]': year, 'end_date[month]': month, 'end_date[day]': day,
    };
  };

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    // Backend (getPresenceReport) needs start_date[year]/[month]/[day] +
    // end_date[...] + type - same fix as DistanceReportPage, this page
    // previously only ever sent a single unread `date` field.
    return reportApi.getPresenceReport(buildParams('json'))
      .then(r => setData(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [date, type]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(() => load(false), { paused: exporting });

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await reportApi.getPresenceReport(buildParams('excel'));
      const fileName = res.data?.file_name;
      if (fileName) window.open(BASE_MEDIA_URL + fileName, '_blank');
      else addToast('خروجی اکسل ایجاد نشد', 'error');
    } catch { addToast('خطا در ایجاد خروجی اکسل', 'error'); }
    setExporting(false);
  };

  return (
    <div className="fade-in">
      <PageHeader title="گزارش حضور"
        action={<Btn variant="secondary" onClick={exportExcel} loading={exporting} disabled={data.length === 0}><DownloadSimple size={14} weight="regular" /> خروجی اکسل</Btn>}
      />
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {[['employee', 'کارمند'], ['company', 'شرکت'], ['department', 'دپارتمان']].map(([id, label]) => (
            <button key={id} onClick={() => setType(id)} style={{ padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${type === id ? '#29AAE1' : '#e2e8f0'}`, background: type === id ? '#EDF3FC' : '#fff', color: type === id ? '#233A7A' : '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{label}</button>
          ))}
        </div>
      </div>
      {loading ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1, 2, 3].map(k => <div key={k} style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}><Skeleton h={16} /></div>)}</div>
        : data.length === 0 ? <EmptyState icon={<ClipboardText size={44} weight="regular" />} title="داده‌ای یافت نشد" />
        : <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '12px 20px', background: '#f8faff', borderBottom: '1px solid #e2e8f0', fontSize: 12, fontWeight: 700, color: '#64748b' }}>
              <span>کارمند</span><span>ورود</span><span>خروج</span><span>وضعیت</span>
            </div>
            {data.map((row, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '13px 20px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#334155', background: i % 2 === 0 ? '#fff' : '#fafbff' }}>
                <span>{row.name || row.username}</span>
                <span>{row.check_in || '—'}</span>
                <span>{row.check_out || '—'}</span>
                <Badge variant={row.present ? 'green' : 'red'}>{row.present ? 'حاضر' : 'غایب'}</Badge>
              </div>
            ))}
          </div>
      }
    </div>
  );
}
